import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  FileText,
  Image,
  FileIcon,
  Download,
  Trash2,
  LayoutGrid,
  List,
  Loader2,
  AlertTriangle,
} from 'lucide-react';
import { cn, formatRelativeTime } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { deleteFile, type CandidateFile, type FileType } from '@/lib/api';
import { downloadFileById } from '@/lib/file-actions';
import { DocumentViewer } from './DocumentViewer';

interface FileListProps {
  files: CandidateFile[];
  candidateId: string;
  isLoading?: boolean;
  className?: string;
}

const FILE_TYPE_LABELS: Record<FileType, string> = {
  cv: 'CV',
  id_document: 'ID',
  certificate: 'Certificate',
  photo: 'Photo',
  other: 'Other',
};

const FILE_TYPE_COLORS: Record<FileType, string> = {
  cv: 'bg-blue-100 text-blue-700',
  id_document: 'bg-purple-100 text-purple-700',
  certificate: 'bg-green-100 text-green-700',
  photo: 'bg-pink-100 text-pink-700',
  other: 'bg-white/10 text-text-secondary',
};

function getFileIcon(mimeType: string | null | undefined) {
  if (mimeType?.startsWith('image/')) {
    return Image;
  }
  if (mimeType === 'application/pdf') {
    return FileText;
  }
  return FileIcon;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function FileList({ files, candidateId, isLoading, className }: FileListProps) {
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const [deleteConfirm, setDeleteConfirm] = useState<CandidateFile | null>(null);
  const [viewerFile, setViewerFile] = useState<CandidateFile | null>(null);
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: (fileId: string) => deleteFile(fileId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['candidateFiles', candidateId] });
      setDeleteConfirm(null);
    },
  });

  const handleDownload = (file: CandidateFile) => {
    void downloadFileById(file.id, file.originalName || file.fileName || 'download');
  };

  const handleFileClick = (file: CandidateFile) => {
    const isPreviewable =
      file.mimeType?.startsWith('image/') || file.mimeType === 'application/pdf';
    if (isPreviewable) {
      setViewerFile(file);
    } else {
      handleDownload(file);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 className="h-8 w-8 text-text-tertiary animate-spin" />
      </div>
    );
  }

  if (files.length === 0) {
    return (
      <div className={cn('flex flex-col items-center justify-center h-48 text-text-tertiary', className)}>
        <FileIcon className="h-12 w-12 mb-2" />
        <p>No files uploaded yet</p>
      </div>
    );
  }

  return (
    <div className={cn('space-y-4', className)}>
      {/* View Mode Toggle */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-text-tertiary">{files.length} file{files.length !== 1 ? 's' : ''}</p>
        <div className="flex items-center gap-1 bg-white/10 rounded-lg p-1">
          <Button
            size="icon"
            variant={viewMode === 'list' ? 'secondary' : 'ghost'}
            className="h-8 w-8"
            onClick={() => setViewMode('list')}
          >
            <List className="h-4 w-4" />
          </Button>
          <Button
            size="icon"
            variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
            className="h-8 w-8"
            onClick={() => setViewMode('grid')}
          >
            <LayoutGrid className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* List View */}
      {viewMode === 'list' && (
        <div className="space-y-2">
          {files.map((file) => {
            const Icon = getFileIcon(file.mimeType);
            return (
              <div
                key={file.id}
                className="flex items-center gap-3 p-3 rounded-lg border border-white/10 bg-bg-tertiary hover:border-white/20 transition-colors cursor-pointer"
                onClick={() => handleFileClick(file)}
              >
                <div className="flex-shrink-0 p-2 rounded-lg bg-white/10">
                  <Icon className="h-6 w-6 text-text-tertiary" />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-text-primary truncate">
                      {file.originalName}
                    </p>
                    <Badge className={cn('text-xs', FILE_TYPE_COLORS[file.fileType] || FILE_TYPE_COLORS.other)}>
                      {FILE_TYPE_LABELS[file.fileType] || 'Other'}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-text-tertiary">
                    <span>{formatFileSize(file.fileSize)}</span>
                    <span>-</span>
                    <span>Uploaded by {file.uploadedBy ? `${file.uploadedBy.firstName} ${file.uploadedBy.lastName}` : 'WhatsApp'}</span>
                    <span>-</span>
                    <span>{formatRelativeTime(file.createdAt)}</span>
                  </div>
                </div>

                <div className="flex items-center gap-1">
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8"
                    onClick={() => handleDownload(file)}
                    title="Download"
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => setDeleteConfirm(file)}
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Grid View */}
      {viewMode === 'grid' && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {files.map((file) => {
            const Icon = getFileIcon(file.mimeType);
            const isImage = file.mimeType?.startsWith('image/');
            return (
              <div
                key={file.id}
                className="group relative rounded-lg border border-white/10 bg-bg-tertiary overflow-hidden hover:border-white/20 transition-colors"
              >
                {/* Preview Area */}
                <div className="aspect-square bg-bg-secondary flex items-center justify-center">
                  {isImage ? (
                    <img
                      src={file.url}
                      alt={file.originalName}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Icon className="h-12 w-12 text-text-tertiary" />
                  )}
                </div>

                {/* File Info */}
                <div className="p-3">
                  <p className="text-sm font-medium text-text-primary truncate" title={file.originalName}>
                    {file.originalName}
                  </p>
                  <div className="flex items-center justify-between mt-1">
                    <Badge className={cn('text-xs', FILE_TYPE_COLORS[file.fileType] || FILE_TYPE_COLORS.other)}>
                      {FILE_TYPE_LABELS[file.fileType] || 'Other'}
                    </Badge>
                    <span className="text-xs text-text-tertiary">{formatFileSize(file.fileSize)}</span>
                  </div>
                </div>

                {/* Hover Actions */}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <Button
                    size="icon"
                    variant="secondary"
                    className="h-10 w-10"
                    onClick={() => handleDownload(file)}
                    title="Download"
                  >
                    <Download className="h-5 w-5" />
                  </Button>
                  <Button
                    size="icon"
                    variant="destructive"
                    className="h-10 w-10"
                    onClick={() => setDeleteConfirm(file)}
                    title="Delete"
                  >
                    <Trash2 className="h-5 w-5" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Document Viewer */}
      <DocumentViewer
        file={viewerFile}
        open={!!viewerFile}
        onOpenChange={(open) => { if (!open) setViewerFile(null); }}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Delete File
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deleteConfirm?.originalName}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteConfirm(null)}
              disabled={deleteMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteConfirm && deleteMutation.mutate(deleteConfirm.id)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
